import { Button, Dialog, DialogButtons, IconTip, Spinner, TextArea } from '@st/theme'
import { match, P } from '@st/pattern'
import { formatFolderEntityName } from './st-folder-display'
import {
  sendClientOrganizer,
  type STSendClientOrganizerMessage,
  type STSendClientOrganizerView
} from './st-send-client-organizer-module'

export function STRequestItemsDialog({
  state,
  send,
  onClose
}: {
  state: STSendClientOrganizerView
  send: (message: STSendClientOrganizerMessage) => void
  onClose: () => void
}) {
  return match(state)
    .with({ status: 'loading' }, () => (
      <Dialog
        title="Request missing documents"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex items-center justify-center py-16">
          <Spinner className="size-8 fill-blue-500 text-blue-100" />
        </div>
      </Dialog>
    ))
    .with({ status: 'loadFailed' }, () => (
      <Dialog
        title="Request missing documents"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex flex-col gap-2 py-8 text-sm text-gray-700">
          <div className="font-semibold text-gray-900">Failed to load email draft.</div>
          <div>Please close this dialog and try again.</div>
        </div>
      </Dialog>
    ))
    .with(
      { status: P.union('loaded', 'saving', 'savingAndClosing', 'sending', 'sent') },
      (state) => (
        <STRequestItemsDialogView
          state={state}
          send={send}
          onClickSend={() => {
            // deleteDraft true means we delete the draft after sending the email
            // this is to avoid leaving a stale draft
            // if we're talking about requesting missing items, this can go stale quickly
            send(sendClientOrganizer({ deleteDraft: true }))
          }}
          onClose={onClose}
        />
      )
    )
    .exhaustive()
}

function STRequestItemsDialogView({
  state,
  send,
  onClickSend,
  onClose
}: {
  state: Exclude<STSendClientOrganizerView, { status: 'loading' | 'loadFailed' }>
  send: (message: STSendClientOrganizerMessage) => void
  onClickSend: () => void
  onClose: () => void
}) {
  const sendState = state.sendState
  const entities = sendState.folder.entities
  const sendActionState = match(state.status)
    .with('loaded', () => ({ type: 'ready', label: 'Send' }) as const)
    .with('saving', () => ({ type: 'saving', label: 'Saving' }) as const)
    .with('savingAndClosing', () => ({ type: 'saving', label: 'Saving' }) as const)
    .with('sending', () => ({ type: 'sending', label: 'Sending' }) as const)
    .with('sent', () => ({ type: 'sent', label: 'Sent' }) as const)
    .exhaustive()

  if (sendState.vars['missingdocumentscount'] == 0) {
    return (
      <Dialog
        title="Request documents"
        subtitle={`According to ${formatFolderEntityName(
          sendState.folder.entities
        )}'s checklist, there aren't any documents which are still waiting to be received.`}
        buttons={
          <DialogButtons>
            <Button onClick={onClose}>Close</Button>
          </DialogButtons>
        }
      />
    )
  }

  return (
    <Dialog
      title="Request missing documents"
      subtitle={match(sendState.folder.clientOrganizer.status)
        .with(P.union('in_progress', 'submitted'), () => {
          return (
            <div>
              The email below contains a list of documents which are missing for{' '}
              {formatFolderEntityName(entities, 'client')}. You can modify the email below or the
              general template in Settings.
            </div>
          )
        })
        .with('not_seen_yet', () => {
          return (
            <div>
              The email below contains a list of documents which are missing for{' '}
              {formatFolderEntityName(entities, 'client')}. You can modify the email below or the
              general template in Settings.
              <br />
              <br />
              If you sent them this organizer (or plan to) we recommend waiting for them to submit
              the organizer before requesting missing documents.
            </div>
          )
        })
        .exhaustive()}
      className="w-full max-w-2xl"
      buttons={
        <DialogButtons>
          <Button
            variant="subtle"
            disabled={state.status == 'sending' || state.status == 'savingAndClosing'}
            onClick={() => {
              // we don't want to save drafts here
              onClose()
            }}
          >
            Close
          </Button>

          <Button
            variant="primary"
            leadingIcon={match(sendActionState)
              .with({ type: P.union('saving', 'sending') }, () => <Spinner className="size-4" />)
              .with({ type: P.union('ready', 'sent') }, () => undefined)
              .exhaustive()}
            disabled={sendActionState.type != 'ready'}
            onClick={() => {
              if (sendActionState.type != 'ready') {
                return
              }
              onClickSend()
            }}
          >
            {sendActionState.label}
          </Button>
        </DialogButtons>
      }
    >
      <div className="">
        <label className="font-semibold">To:</label>
        <div className="flex flex-row gap-1">
          {state.editedRecipients.map((recipient, index) => {
            return (
              <div key={recipient.email}>
                {recipient.email + (index == state.editedRecipients.length - 1 ? '' : ',')}
              </div>
            )
          })}
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <label className="font-semibold">Subject:</label>
          <IconTip
            icon="info"
            body="You can change the subject in Settings → Templates"
            className="text-gray-500"
          />
        </div>
        <div className="rounded-lg px-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-200">
          {sendState.subject}
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <label className="font-semibold">Message:</label>
        <TextArea
          className="min-h-96"
          placeholder="Send a message"
          readOnly={
            state.status == 'savingAndClosing' ||
            state.status == 'sending' ||
            state.status == 'sent'
          }
          value={state.body}
          onChange={(body) => send({ type: 'bodyChanged', body })}
        />
      </div>
    </Dialog>
  )
}
